import { useState, useRef, useCallback } from 'react'
import {
  Upload, FileText, CheckCircle2, XCircle, AlertTriangle,
  Loader2, Download, Users, X,
} from 'lucide-react'
import { getUsers, createUser } from '@/api/users'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'

type RowStatus = 'ok' | 'dup-db' | 'dup-csv' | 'processing' | 'done' | 'error' | 'skipped'

interface ParsedRow {
  index: number
  name: string
  employeeNo: string
  email?: string
  phone?: string
  idNumber?: string
  status: RowStatus
  errorMsg?: string
}

const TEMPLATE = `nombre,cedula,email,telefono
Juan Pérez,1234567890,juan@email.com,3001234567
María López,0987654321,,`

function downloadTemplate() {
  const blob = new Blob([TEMPLATE], { type: 'text/csv;charset=utf-8' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'plantilla_miembros.csv'
  a.click()
  URL.revokeObjectURL(a.href)
}

function findCol(headers: string[], ...variants: string[]) {
  return headers.findIndex(h => variants.some(v => h.includes(v)))
}

function parseCSV(text: string): ParsedRow[] | null {
  const lines = text.trim().split(/\r?\n/).filter(l => l.trim())
  if (lines.length < 2) return null
  const delimiter = lines[0].includes(';') ? ';' : ','
  const headers = lines[0].split(delimiter).map(h =>
    h.trim().toLowerCase().replace(/[^a-záéíóúñ_]/g, '').replace(/[áéíóúñ]/g, '')
  )
  const nameIdx  = findCol(headers, 'nombre', 'name')
  const empIdx   = findCol(headers, 'cedula', 'empleado', 'employee', 'numero', 'no')
  const emailIdx = findCol(headers, 'email', 'correo')
  const phoneIdx = findCol(headers, 'telefono', 'phone', 'celular')
  const idIdx    = findCol(headers, 'cedula', 'documento', 'id')
  if (nameIdx === -1 || empIdx === -1) return null
  return lines.slice(1).flatMap((line, i) => {
    const cells = line.split(delimiter).map(c => c.trim().replace(/^"|"$/g, ''))
    const name       = cells[nameIdx]?.trim() ?? ''
    const employeeNo = cells[empIdx]?.trim() ?? ''
    if (!name || !employeeNo) return []
    return [{
      index: i,
      name,
      employeeNo,
      email:      emailIdx >= 0 ? cells[emailIdx]?.trim() || undefined : undefined,
      phone:      phoneIdx >= 0 ? cells[phoneIdx]?.trim() || undefined : undefined,
      idNumber:   idIdx    >= 0 ? cells[idIdx]?.trim()    || undefined : undefined,
      status: 'ok' as RowStatus,
    }]
  })
}

const STATUS_BADGE: Record<RowStatus, { label: string; className: string }> = {
  ok:         { label: 'Listo',         className: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-200 dark:border-emerald-500/20' },
  'dup-db':   { label: 'Ya existe',     className: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20' },
  'dup-csv':  { label: 'Duplicado',     className: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20' },
  processing: { label: 'Importando…',   className: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/20' },
  done:       { label: 'Importado',     className: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-200 dark:border-emerald-500/20' },
  error:      { label: 'Error',         className: 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border-red-200 dark:border-red-500/20' },
  skipped:    { label: 'Omitido',       className: 'bg-border/60 text-text-muted border-border' },
}

export function BulkImport() {
  const [rows, setRows]       = useState<ParsedRow[] | null>(null)
  const [dragging, setDragging] = useState(false)
  const [importing, setImporting] = useState(false)
  const [done, setDone]       = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const processFile = useCallback(async (file: File) => {
    const text = await file.text()
    const parsed = parseCSV(text)
    if (!parsed) {
      toast.error('No se pudo leer el archivo. Verifica que tenga columnas "nombre" y "cedula".')
      return
    }
    // mark within-CSV duplicates
    const seen = new Map<string, number>()
    parsed.forEach(r => {
      if (seen.has(r.employeeNo)) r.status = 'dup-csv'
      else seen.set(r.employeeNo, r.index)
    })
    // compare against DB
    try {
      const { users } = await getUsers()
      const dbNos = new Set(users.map(u => u.employee_no))
      parsed.forEach(r => {
        if (r.status === 'ok' && dbNos.has(r.employeeNo)) r.status = 'dup-db'
      })
    } catch {
      toast.error('No se pudo verificar duplicados contra la base de datos')
    }
    setRows(parsed)
    setDone(false)
  }, [])

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  async function runImport() {
    if (!rows) return
    setImporting(true)
    let created = 0, skipped = 0, errors = 0
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i]
      if (r.status === 'dup-db' || r.status === 'dup-csv') {
        skipped++
        setRows(prev => prev && prev.map((x, j) => j === i ? { ...x, status: 'skipped' } : x))
        continue
      }
      setRows(prev => prev && prev.map((x, j) => j === i ? { ...x, status: 'processing' } : x))
      try {
        await createUser({
          employeeNo: r.employeeNo,
          name: r.name,
          email: r.email,
          phone: r.phone,
          idNumber: r.idNumber,
          longTerm: true,
        })
        created++
        setRows(prev => prev && prev.map((x, j) => j === i ? { ...x, status: 'done' } : x))
      } catch (e) {
        errors++
        const msg = e instanceof ApiError ? e.message : 'Error al crear'
        setRows(prev => prev && prev.map((x, j) => j === i ? { ...x, status: 'error', errorMsg: msg } : x))
      }
    }
    setImporting(false)
    setDone(true)
    if (errors === 0) toast.success(`${created} miembros importados · ${skipped} omitidos`)
    else toast.error(`${created} importados · ${skipped} omitidos · ${errors} errores`)
  }

  const validCount  = rows?.filter(r => r.status === 'ok').length ?? 0
  const skipCount   = rows?.filter(r => r.status === 'dup-db' || r.status === 'dup-csv').length ?? 0

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-text-muted text-xs font-medium uppercase tracking-wide">Importación masiva</h2>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary border border-border rounded-lg px-2.5 py-1.5 hover:bg-surface transition-colors cursor-pointer"
        >
          <Download className="w-3 h-3" />
          Descargar plantilla
        </button>
      </div>

      {!rows ? (
        /* ── Drop zone ── */
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors ${
            dragging
              ? 'border-accent bg-accent/5'
              : 'border-border hover:border-accent/50 hover:bg-surface'
          }`}
        >
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${
            dragging ? 'bg-accent/15' : 'bg-background border border-border'
          }`}>
            <Upload className={`w-5 h-5 ${dragging ? 'text-accent' : 'text-text-muted'}`} />
          </div>
          <div className="text-center">
            <p className="text-text-primary text-sm font-semibold">
              {dragging ? 'Suelta el archivo aquí' : 'Arrastra un CSV o haz clic'}
            </p>
            <p className="text-text-muted text-xs mt-1">
              Columnas requeridas: <span className="font-mono text-text-primary">nombre</span> y{' '}
              <span className="font-mono text-text-primary">cedula</span>
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.tsv,.txt"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      ) : (
        /* ── Preview table ── */
        <div className="bg-surface border border-border rounded-2xl overflow-hidden">
          {/* Table header bar */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-text-muted" />
              <span className="text-text-primary text-sm font-semibold">{rows.length} filas encontradas</span>
              {skipCount > 0 && (
                <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 px-2 py-0.5 rounded-full">
                  {skipCount} duplicado{skipCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {!done && (
              <button
                onClick={() => { setRows(null) }}
                disabled={importing}
                className="text-text-muted hover:text-text-primary transition-colors disabled:opacity-40 cursor-pointer"
                aria-label="Quitar archivo"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm">
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-text-muted text-[10px] font-bold uppercase tracking-widest">Nombre</th>
                  <th className="text-left px-4 py-2 text-text-muted text-[10px] font-bold uppercase tracking-widest">Cédula</th>
                  <th className="text-left px-4 py-2 text-text-muted text-[10px] font-bold uppercase tracking-widest hidden sm:table-cell">Email</th>
                  <th className="text-left px-4 py-2 text-text-muted text-[10px] font-bold uppercase tracking-widest">Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const badge = STATUS_BADGE[r.status]
                  const muted = r.status === 'dup-db' || r.status === 'dup-csv' || r.status === 'skipped'
                  return (
                    <tr key={i} className={`border-b border-border/50 last:border-0 ${muted ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2.5 text-text-primary text-xs font-medium">{r.name}</td>
                      <td className="px-4 py-2.5 text-text-muted text-xs font-mono">{r.employeeNo}</td>
                      <td className="px-4 py-2.5 text-text-muted text-xs hidden sm:table-cell">{r.email ?? '—'}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          {r.status === 'processing' && <Loader2 className="w-3 h-3 text-blue-400 animate-spin" />}
                          {r.status === 'done'       && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                          {r.status === 'error'      && <XCircle className="w-3 h-3 text-red-400" />}
                          {(r.status === 'dup-db' || r.status === 'dup-csv') && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.className}`}>
                            {r.errorMsg ?? badge.label}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Footer with action */}
          {!done && (
            <div className="border-t border-border px-5 py-3 flex items-center justify-between gap-3">
              <p className="text-text-muted text-xs">
                {validCount > 0
                  ? <><span className="text-text-primary font-semibold">{validCount}</span> para importar · <span className="text-amber-600 dark:text-amber-400">{skipCount}</span> se omitirán</>
                  : <span className="text-amber-600 dark:text-amber-400">No hay filas válidas para importar</span>
                }
              </p>
              <button
                onClick={runImport}
                disabled={importing || validCount === 0}
                className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Importando…</>
                  : <><Users className="w-4 h-4" /> Importar {validCount} miembros</>
                }
              </button>
            </div>
          )}

          {done && (
            <div className="border-t border-border px-5 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span className="text-text-primary font-semibold">
                  {rows.filter(r => r.status === 'done').length} importados
                </span>
                {rows.filter(r => r.status === 'error').length > 0 && (
                  <span className="text-red-600 dark:text-red-400">· {rows.filter(r => r.status === 'error').length} errores</span>
                )}
              </div>
              <button
                onClick={() => { setRows(null); setDone(false) }}
                className="flex items-center gap-2 border border-border text-text-primary text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-background transition-colors cursor-pointer"
              >
                Nueva importación
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
